import { useQuery } from '@tanstack/react-query'
import { Target, CheckCircle, AlertCircle } from 'lucide-react'
import { goalsApi } from '../api/client'
import './GoalsProgress.css'

interface Goal {
  id: string
  name: string
  type: string
  target: number
  current: number
  unit: string
  active: boolean
}

function GoalCard({ goal }: { goal: Goal }) {
  const progress = Math.min((goal.current / goal.target) * 100, 100)
  const isOnTrack = goal.current >= goal.target * 0.8 // 80% or more is on track
  const isComplete = goal.current >= goal.target

  const getProgressClass = () => {
    if (isComplete) return 'complete'
    if (isOnTrack) return 'on-track'
    return 'behind'
  }

  const formatValue = (value: number, unit: string) => {
    if (unit === '%') return `${value.toFixed(1)}%`
    if (unit === '$') return `$${value.toFixed(0)}`
    return `${value.toFixed(1)} ${unit}`
  }

  return (
    <div className={`goal-card ${getProgressClass()}`}>
      <div className="goal-header">
        <span className="goal-name">{goal.name}</span>
        {isComplete ? (
          <CheckCircle size={16} className="goal-status-icon complete" />
        ) : !isOnTrack ? (
          <AlertCircle size={16} className="goal-status-icon behind" />
        ) : null}
      </div>

      <div className="goal-progress-bar">
        <div className="progress-track">
          <div
            className={`progress-fill ${getProgressClass()}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="goal-values">
        <span className="current-value">{formatValue(goal.current, goal.unit)}</span>
        <span className="target-value">/ {formatValue(goal.target, goal.unit)}</span>
      </div>
    </div>
  )
}

export function GoalsProgress() {
  const { data: goals, isLoading } = useQuery({
    queryKey: ['goals'],
    queryFn: goalsApi.getAll,
    refetchInterval: 30000,
  })

  const activeGoals = goals?.filter((g) => g.active) ?? []
  const completedCount = activeGoals.filter((g) => g.current >= g.target).length

  return (
    <div className="card goals-card">
      <div className="card-header">
        <h3 className="card-title">
          <Target size={20} className="icon" />
          Goals
        </h3>
        {activeGoals.length > 0 && (
          <span className={`card-badge ${completedCount === activeGoals.length ? 'success' : 'warning'}`}>
            {completedCount}/{activeGoals.length} complete
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="loading">
          <div className="spinner" />
        </div>
      ) : activeGoals.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <Target size={32} aria-hidden="true" />
          </div>
          <div className="empty-text">No active goals</div>
        </div>
      ) : (
        <div className="goals-list">
          {activeGoals.map((goal) => (
            <GoalCard key={goal.id} goal={goal} />
          ))}
        </div>
      )}
    </div>
  )
}
